import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useAuth } from '../../context/AuthContext';
import { useBrand } from '../../context/BrandContext';
import useObjectDefinitions from '../../hooks/useObjectDefinitions';
import UserMenu from '../UserMenu';
import { FileText, Globe, Building2, FolderOpen, Sparkles, Settings, PanelLeftClose, PanelLeft, Shield, Workflow as WorkflowIcon, Bot, Target } from 'lucide-react';

const staticNavSections = [
  {
    items: [
      { path: '/chat', label: 'Able Assistant', icon: Sparkles, feature: 'chat' },
    ],
  },
  {
    label: 'Build',
    items: [
      { path: '/forms', label: 'Forms', icon: FileText, feature: 'forms' },
      { path: '/sites', label: 'Sites', icon: Globe, feature: 'pages' },
      { path: '/workflows', label: 'Workflows', icon: WorkflowIcon, feature: 'workflows' },
      { path: '/chatbots', label: 'Chatbots', icon: Bot, feature: 'chatbots' },
      { path: '/prospecting', label: 'Prospecting', icon: Target, feature: 'prospecting' },
    ],
  },
];

const POSTHOG_WORKFLOW_FLAG = 'experiment_new-module_workflow-builder_temp';
const POSTHOG_CHATBOT_FLAG = 'experiment_new-module_chatbot-builder_temp';
const POSTHOG_PROSPECTING_FLAG = 'experiment_new-module_prospecting_temp';

function Sidebar() {
  const { isAuthenticated, user, organization } = useAuth();
  const { brandConfig } = useBrand();
  const location = useLocation();
  const workflowFlagEnabled = useFeatureFlagEnabled(POSTHOG_WORKFLOW_FLAG);
  const chatbotFlagEnabled = useFeatureFlagEnabled(POSTHOG_CHATBOT_FLAG);
  const prospectingFlagEnabled = useFeatureFlagEnabled(POSTHOG_PROSPECTING_FLAG);
  const [manualCollapse, setManualCollapse] = useState(null);
  const { objectDefinitions } = useObjectDefinitions();

  const shouldAutoCollapse =
    (location.pathname === '/chat' || location.pathname.startsWith('/chat/')) ||
    /^\/(forms|pages|sites|workflows|chatbots|prospecting)\/[^/]+/.test(location.pathname);
  const collapsed = manualCollapse !== null ? manualCollapse : shouldAutoCollapse;
  const isManualToggle = manualCollapse !== null;

  useEffect(() => {
    setManualCollapse(null);
  }, [shouldAutoCollapse]);

  if (!isAuthenticated) return null;

  // Build category-based nav items from org settings and object definitions
  const categories = organization?.settings?.navbar_manage_items_config?.categories || ['CRM'];

  const manageItems = categories.map((category) => {
    const categoryObjects = objectDefinitions.filter((od) => (od.category || 'CRM') === category);
    const children = categoryObjects.map((od) => ({
      path: `/data/${od.slug_plural || od.slug}`,
      label: od.name.endsWith('s') ? od.name : `${od.name}s`,
    }));
    // Always add Pipelines to CRM
    if (category === 'CRM') {
      children.push({ path: '/data/pipelines', label: 'Pipelines' });
    }
    const firstChild = children.length > 0 ? children[0].path : undefined;
    return {
      // Non-CRM categories don't have their own route — they navigate to their first child
      path: category === 'CRM' ? '/data/companies' : firstChild,
      label: category,
      icon: category === 'CRM' ? Building2 : FolderOpen,
      feature: 'crm',
      defaultChild: firstChild || (category === 'CRM' ? '/data/companies' : undefined),
      children,
      // Store child paths so isActivePage can check them for non-CRM categories
      _childPaths: children.map((c) => c.path),
    };
  });

  const navSections = [
    ...staticNavSections,
    {
      label: 'Manage',
      items: manageItems,
    },
  ];

  const isActivePage = (item) => {
    const path = item.path || item;
    if (path === '/chat') {
      return location.pathname === '/' || location.pathname === '/chat' || location.pathname.startsWith('/chat/');
    }
    if (path === '/forms') {
      return location.pathname === '/forms' || location.pathname.startsWith('/forms/');
    }
    if (path && (location.pathname === path || location.pathname.startsWith(path + '/'))) {
      return true;
    }
    // For categories without their own route, check if any child is active
    if (item._childPaths) {
      return item._childPaths.some((cp) => location.pathname === cp || location.pathname.startsWith(cp + '/'));
    }
    return false;
  };

  return (
    <aside className={`${collapsed ? 'w-16' : 'w-60'} h-screen flex flex-col bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-700 ${isManualToggle ? 'transition-[width] duration-200' : ''} overflow-hidden`}>
      {/* Brand */}
      <div className={`h-14 flex items-center ${collapsed ? 'justify-center px-2' : 'px-5'} border-b border-gray-100 dark:border-gray-700`}>
        <Link to="/" className="flex items-center min-w-0">
          <div className="w-7 h-7  flex items-center justify-center flex-shrink-0">
            <img src={brandConfig.icon} alt={brandConfig.name} className="w-full" />
          </div>
          {!collapsed && (
            <span className="text-[15px] font-semibold text-gray-900 dark:text-white truncate">
              <img src={brandConfig.logo} alt={brandConfig.name} className="object-contain h-[30px]" />
            </span>
          )}
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-3 px-3 overflow-y-auto">
        {navSections.map((section, sectionIndex) => {
          const visibleItems = section.items.filter(item =>
            brandConfig.features[item.feature] && (item.feature !== 'workflows' || workflowFlagEnabled) && (item.feature !== 'chatbots' || chatbotFlagEnabled) && (item.feature !== 'prospecting' || prospectingFlagEnabled)
          );
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.label || sectionIndex} className={sectionIndex > 0 ? 'mt-5' : ''}>
              {section.label && !collapsed && (
                <div className="px-3 pb-1.5 text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-gray-500">
                  {section.label}
                </div>
              )}
              {collapsed && sectionIndex > 0 && (
                <div className="mx-2 mb-2 border-t border-gray-100 dark:border-gray-700" />
              )}
              <div className="space-y-0.5">
                {visibleItems.map(item => {
                  const active = isActivePage(item);
                  const Icon = item.icon;
                  const linkTo = item.defaultChild || item.path;
                  if (!linkTo) return null; // Empty category with no children
                  return (
                    <div key={item.path || item.label}>
                      <Link
                        to={linkTo}
                        title={collapsed ? item.label : undefined}
                        className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-[13px] font-medium transition-colors ${
                          active
                            ? 'text-gray-900 bg-gray-100 dark:text-white dark:bg-gray-800'
                            : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800'
                        }`}
                      >
                        <Icon className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={active ? 2 : 1.75} />
                        {!collapsed && <span className="ml-2.5">{item.label}</span>}
                      </Link>
                      {/* Sub-navigation children */}
                      {item.children && active && !collapsed && (
                        <div className="ml-[23px] mt-1 mb-1 border-l border-gray-200 dark:border-gray-700">
                          {item.children.map(child => {
                            const childActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                            return (
                              <Link
                                key={child.path}
                                to={child.path}
                                className={`relative flex items-center pl-4 pr-3 py-1.5 text-[13px] font-medium transition-colors ${
                                  childActive
                                    ? 'text-gray-900 dark:text-white'
                                    : 'text-gray-400 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300'
                                }`}
                              >
                                {childActive && (
                                  <span className="absolute left-[-1.5px] top-1.5 bottom-1.5 w-[3px] rounded-full bg-brand-500" />
                                )}
                                {child.label}
                              </Link>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-gray-100 dark:border-gray-700 p-3 space-y-0.5">
        {user?.is_superadmin && (
          <Link
            to="/admin"
            title={collapsed ? 'Admin' : undefined}
            className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-[13px] font-medium transition-colors ${
              isActivePage('/admin')
                ? 'text-gray-900 bg-gray-100 dark:text-white dark:bg-gray-800'
                : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800'
            }`}
          >
            <Shield className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={isActivePage('/admin') ? 2 : 1.75} />
            {!collapsed && <span className="ml-2.5">Admin</span>}
          </Link>
        )}
        <Link
          to="/settings"
          title={collapsed ? 'Settings' : undefined}
          className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-[13px] font-medium transition-colors ${
            isActivePage('/settings')
              ? 'text-gray-900 bg-gray-100 dark:text-white dark:bg-gray-800'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800'
          }`}
        >
          <Settings className="w-[18px] h-[18px] flex-shrink-0" strokeWidth={1.75} />
          {!collapsed && <span className="ml-2.5">Settings</span>}
        </Link>
        <button
          onClick={() => setManualCollapse(prev => prev !== null ? !prev : !shouldAutoCollapse)}
          className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-3'} py-2 rounded-lg text-[13px] font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800 transition-colors w-full`}
        >
          {collapsed ? <PanelLeft className="w-[18px] h-[18px]" strokeWidth={1.75} /> : <PanelLeftClose className="w-[18px] h-[18px]" strokeWidth={1.75} />}
          {!collapsed && <span className="ml-2.5">Collapse</span>}
        </button>
        <div className={`${collapsed ? 'px-0' : 'px-1'} pt-2`}>
          <div className={`${collapsed ? 'flex justify-center' : ''} pt-2`}>
            <UserMenu compact={collapsed} />
          </div>
        </div>
      </div>
    </aside>
  );
}

export default function SidebarLayout({ children }) {
  const location = useLocation();
  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-900 text-gray-900">
      <Sidebar />
      <main className="flex-1 h-screen overflow-auto">
        <div key={location.pathname} className="animate-fadeIn h-full">
          {children}
        </div>
      </main>
    </div>
  );
}
